import React, { useState, useEffect, useRef, useCallback } from 'react'
import { ChevronLeft, ChevronRight, X, Waves, Mountain, Anchor, Camera, Utensils, Wind } from 'lucide-react'
import Reveal from '../components/Reveal.jsx'

const ACTIVITIES = Array.isArray(window.wpData?.homeActivities) ? window.wpData.homeActivities : []

const CATEGORY_ICONS = { Water: Waves, Adventure: Mountain, Cultural: Anchor, Photo: Camera, Dining: Utensils, Wellness: Wind }
const MOCK_COLORS = ['#b8ccd8', '#a8d4c0', '#d4c4a8', '#c8b8d8', '#b8d4c8', '#d4b8b8', '#a8c8d4', '#c4d4a8']

function ActivityModal({ item, colorIdx, onClose }) {
  const [imgIdx, setImgIdx] = useState(0)
  const hasImages = item.images?.length > 0
  const Icon = CATEGORY_ICONS[item.category] ?? Anchor

  const prev = useCallback(() => setImgIdx(i => (i - 1 + item.images.length) % item.images.length), [item.images.length])
  const next = useCallback(() => setImgIdx(i => (i + 1) % item.images.length), [item.images.length])

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft'  && hasImages) prev()
      if (e.key === 'ArrowRight' && hasImages) next()
    }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = '' }
  }, [prev, next, onClose, hasImages])

  return (
    <div className="hp-act-modal__backdrop" onClick={onClose}>
      <div className="hp-act-modal" onClick={e => e.stopPropagation()}>
        <button className="hp-act-modal__close" onClick={onClose}><X size={18} /></button>

        <div
          className="hp-act-modal__img-wrap"
          style={{ background: hasImages ? undefined : MOCK_COLORS[colorIdx % MOCK_COLORS.length] }}
        >
          {hasImages ? (
            <>
              <img src={item.images[imgIdx]} alt={item.title} className="hp-act-modal__img" />
              {item.images.length > 1 && (
                <>
                  <button className="hp-act-modal__nav hp-act-modal__nav--prev" onClick={e => { e.stopPropagation(); prev() }}>
                    <ChevronLeft size={20} />
                  </button>
                  <button className="hp-act-modal__nav hp-act-modal__nav--next" onClick={e => { e.stopPropagation(); next() }}>
                    <ChevronRight size={20} />
                  </button>
                  <div className="hp-act-modal__counter">{imgIdx + 1} / {item.images.length}</div>
                </>
              )}
            </>
          ) : (
            <div className="hp-act-modal__mock-icon">
              <Icon size={48} strokeWidth={1} />
            </div>
          )}
        </div>

        <div className="hp-act-modal__body">
          {item.category && <span className="hp-act-modal__tag">{item.category}</span>}
          <h3 className="hp-act-modal__title">{item.title}</h3>
          {item.description && <p className="hp-act-modal__desc">{item.description}</p>}
        </div>
      </div>
    </div>
  )
}

export default function ActivitiesSection() {
  const list = ACTIVITIES
  if (!list.length) return null

  const scrollRef  = useRef(null)
  const hovered    = useRef(false)
  const [activeItem, setActiveItem] = useState(null)

  // Scroll 1 card at a time
  const scroll = useCallback((dir) => {
    const el = scrollRef.current
    if (!el) return
    const card = el.querySelector('.hp-act-card')
    const step = card ? card.offsetWidth + 24 : 280
    el.scrollBy({ left: dir * step, behavior: 'smooth' })
  }, [])

  // Auto-scroll
  useEffect(() => {
    const id = setInterval(() => {
      if (hovered.current || !scrollRef.current) return
      const el = scrollRef.current
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4
      if (atEnd) {
        el.scrollTo({ left: 0, behavior: 'smooth' })
      } else {
        scroll(1)
      }
    }, 4500)
    return () => clearInterval(id)
  }, [scroll])

  return (
    <>
      <section className="hp-section hp-section--ivory hp-act-section">
        <div className="hp-inner">
          <Reveal variant="fade-up">
            <div className="hp-act__header">
              <div>
                <div className="hp-eyebrow">Activities &amp; Experiences</div>
                <h2 className="hp-title">Life on the Bay</h2>
                <div className="hp-divider" />
              </div>
              <div className="hp-act__nav">
                <button className="hp-itin__btn" onClick={() => scroll(-1)} aria-label="Previous">
                  <ChevronLeft size={18} strokeWidth={1.5} />
                </button>
                <button className="hp-itin__btn" onClick={() => scroll(1)} aria-label="Next">
                  <ChevronRight size={18} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </Reveal>

          <div
            className="hp-act__carousel"
            ref={scrollRef}
            onMouseEnter={() => { hovered.current = true }}
            onMouseLeave={() => { hovered.current = false }}
          >
            {list.map((item, i) => {
              const Icon = CATEGORY_ICONS[item.category] ?? Anchor
              const hasImg = item.images?.length > 0
              return (
                <div
                  key={i}
                  className="hp-act-card"
                  onClick={() => setActiveItem({ item, colorIdx: i })}
                >
                  <div
                    className="hp-act-card__img"
                    style={{ background: hasImg ? undefined : MOCK_COLORS[i % MOCK_COLORS.length] }}
                  >
                    {hasImg
                      ? <img src={item.images[0]} alt={item.title} />
                      : <div className="hp-act-card__mock-icon"><Icon size={28} strokeWidth={1} /></div>
                    }
                  </div>
                  <div className="hp-act-card__body">
                    {item.category && <span className="hp-act-card__cat">{item.category}</span>}
                    <h4 className="hp-act-card__title">{item.title}</h4>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {activeItem && (
        <ActivityModal
          item={activeItem.item}
          colorIdx={activeItem.colorIdx}
          onClose={() => setActiveItem(null)}
        />
      )}
    </>
  )
}
